
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GroundingLink, NewsResponse, WeatherData, AirQualityData, MarineData } from '../types';
import DisasterMap from './DisasterMap';
import { fetchQuickActionInfo, fetchRainfallWarning } from '../services/geminiService';
import { fetchTegalWeather, fetchAirQuality, fetchMarineForecast, fetchWeatherByCoordinates } from '../services/weatherService';
import { sendNotification } from '../services/notificationService';
import WeatherValidation from './WeatherValidation';
import WeatherCard from './WeatherCard';
import { RainfallWarning } from '../types';

interface DashboardProps {
  news: NewsResponse;
  loading: boolean;
  onRefresh: () => void;
  notificationsEnabled: boolean;
  toggleNotifications: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ 
  news, 
  loading, 
  onRefresh, 
  notificationsEnabled, 
  toggleNotifications 
}) => {
  const [activeAction, setActiveAction] = useState<'rob' | 'weather' | null>(null);
  const [actionContent, setActionContent] = useState<string>('');
  const [actionLoading, setActionLoading] = useState(false);
  const [weatherLocation, setWeatherLocation] = useState<string>('Slawi');
  const [weatherSubtext, setWeatherSubtext] = useState<string>('Laporan Bojong.');
  const [isUserLocation, setIsUserLocation] = useState(false);
  const [userLocationWeather, setUserLocationWeather] = useState<WeatherData | null>(null);
  const [kotaWeather, setKotaWeather] = useState<WeatherData | null>(null);
  const [kabWeather, setKabWeather] = useState<WeatherData | null>(null);
  const [aqiKota, setAqiKota] = useState<AirQualityData | null>(null);
  const [aqiKab, setAqiKab] = useState<AirQualityData | null>(null);
  const [marine, setMarine] = useState<MarineData | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [warning, setWarning] = useState<RainfallWarning | null>(null);
  const lastNotifiedStatus = useRef<string | null>(null);
  
  // Helper to determine validation weather context
  const currentWeather = weatherLocation.toLowerCase().includes('kab') ||  
                        ['slawi', 'guci', 'adiwerna', 'bumijawa'].some(x => weatherLocation.toLowerCase().includes(x)) 
                        ? kabWeather 
                        : kotaWeather;

  const fetchWeathers = async (force: boolean = false) => {
    setWeatherLoading(true);
    try {
      const [kota, kab, marineData, aqiKotaData, aqiKabData] = await Promise.all([
        fetchTegalWeather('Kota Tegal', force),
        fetchTegalWeather('Kab. Tegal', force),
        fetchMarineForecast(force),
        fetchAirQuality('Kota Tegal', force),
        fetchAirQuality('Kab. Tegal', force)
      ]);
      setKotaWeather(kota);
      setKabWeather(kab);
      setMarine(marineData);
      setAqiKota(aqiKotaData);
      setAqiKab(aqiKabData);
    } catch (error) {
      console.error("Failed to fetch weather data:", error);
    } finally {
      setWeatherLoading(false);
    }
  };

  const fetchWarning = async (force: boolean = false) => {
    try {
      const warn = await fetchRainfallWarning(force);
      setWarning(warn);
    } catch (e) {
      console.error("Failed to fetch warning:", e);
    }
  };

  const fetchUserLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const data = await fetchWeatherByCoordinates(latitude, longitude, 'Lokasi Anda');
            setUserLocationWeather(data);
          } catch (e) {
            console.error("Failed to fetch user location weather", e);
          }
        },
        (error) => {
          console.log("Location access denied or error:", error);
        }
      );
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const startCycling = () => {
      setIsUserLocation(false);
    };

    // Default to Tegal directly to avoid "Geolocation on page load" Trust & Safety issue
    startCycling();
    fetchWeathers();
    fetchWarning(); // Fetch warning only once on mount (cache handles duration)
    fetchUserLocation();
    
    // Refresh weather every 1 minute
    intervalId = setInterval(() => {
      fetchWeathers();
      fetchUserLocation();
    }, 60000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const handleManualRefresh = () => {
    fetchWeathers(true);
    fetchWarning(true);
    onRefresh();
  };

  // Notification Logic for Weather Warnings
  useEffect(() => {
    if (notificationsEnabled && warning?.status && warning.status !== 'Aman') {
      if (lastNotifiedStatus.current !== warning.status) {
        if (Notification.permission === 'granted') {
           sendNotification(`⚠️ Status ${warning.status}: Curah Hujan Tinggi`, {
             body: warning.message || 'Waspada potensi cuaca ekstrem di wilayah Tegal. Cek aplikasi untuk detail.',
             icon: '/icon-192x192.png',
             tag: 'weather-warning',
             requireInteraction: true
           });
        }
        lastNotifiedStatus.current = warning.status;
      }
    }
  }, [warning, notificationsEnabled]);

  const handleQuickAction = async (type: 'rob' | 'weather') => {
    setActiveAction(type);
    setActionLoading(true);
    setActionContent('');
    try {
      const info = await fetchQuickActionInfo(type, type === 'weather' ? weatherLocation : undefined);
      setActionContent(info);
    } catch (err) {
      setActionContent('Gagal memuat informasi. Koneksi sedang sibuk.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleShareNews = async () => {
    if (!news.text) return;

    let shareContent = `📢 *Kabar Terkini Siaga Tegal*\n\n${news.text.replace(/\*\*/g, '*')}`;

    if (news.links && news.links.length > 0) {
      shareContent += `\n\n📚 *Sumber Berita:*`;
      news.links.forEach(link => {
        shareContent += `\n- ${link.title}: ${link.uri}`;
      });
    }

    shareContent += `\n\n📍 _Pantau terus update terkini di aplikasi Siaga Tegal._`;

    const shareData = {
      title: 'Kabar Terkini - Siaga Tegal',
      text: shareContent,
      url: 'https://siagategal.web.id'
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback to WhatsApp for desktop or unsupported browsers
        const waText = encodeURIComponent(`${shareData.text}\n\n${shareData.url}`);
        window.open(`https://wa.me/?text=${waText}`, '_blank');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const getTimeLabel = (timestamp?: number) => {
    if (!timestamp) return 'Update Baru';
    const diff = Math.floor((Date.now() - timestamp) / 60000);
    if (diff < 1) return 'Baru saja';
    if (diff < 60) return `${diff}m yang lalu`;
    return `${Math.floor(diff/60)}j yang lalu`;
  };

  return (
    <div className="overflow-hidden space-y-6 max-w-full duration-500 animate-in fade-in">
      {/* Hero Alert Status */}
      <div className={`rounded-[2rem] p-5 sm:p-6 text-white shadow-xl relative overflow-hidden transition-all duration-500 ${
        weatherLoading ? 'bg-gray-700' :
        warning?.status === 'Siaga' ? 'bg-orange-600' :
        warning?.status === 'Waspada' ? 'bg-yellow-600' :
        warning?.status === 'Awas' ? 'bg-red-600' :
        'bg-gradient-to-br from-red-600 to-red-800'
      }`}>
        <div className="flex relative z-10 flex-col gap-4 justify-between items-start sm:flex-row">
          <div>
            <h2 className="mb-1 text-xl font-black sm:text-2xl">
              {weatherLoading ? 'Memeriksa Status...' : 
               warning?.status && warning.status !== 'Aman' ? `Status: ${warning.status.toUpperCase()}` : 'Kondisi Tegal'}
            </h2>
            <p className="text-white/80 text-[10px] mb-4 font-bold uppercase tracking-wider">Laporan Kota & Kabupaten</p>
            <div className="inline-flex items-center bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-xl text-xs font-bold border border-white/30">
              <span className={`w-2 h-2 rounded-full mr-2 ${weatherLoading ? 'bg-gray-400 animate-pulse' : 
                warning?.status === 'Siaga' ? 'bg-orange-200 animate-pulse' :
                warning?.status === 'Waspada' ? 'bg-yellow-200 animate-pulse' :
                warning?.status === 'Awas' ? 'bg-red-200 animate-pulse' :
                'bg-green-400'
              }`}></span>
              {weatherLoading ? 'Sedang memuat data...' : warning?.message || 'Kondisi Terpantau Aman'}
            </div>
            {!weatherLoading && warning?.status && warning.status !== 'Aman' && (
              <div className="mt-2">
                <a href="https://staklim-jateng.bmkg.go.id/pdcht.php" target="_blank" rel="noopener noreferrer" className="text-[10px] bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg font-bold uppercase transition">
                  Info BMKG ↗
                </a>
              </div>
            )}
          </div>
          <button 
            onClick={toggleNotifications}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border ${
              notificationsEnabled 
              ? 'text-red-700 bg-white border-white shadow-lg' 
              : 'text-white bg-transparent border-white/40'
            }`}
          >
            <span>{notificationsEnabled ? '🔔 Notif Aktif' : '🔕 Notif Mati'}</span>
          </button>
        </div>
        <div className="absolute -right-6 -bottom-6 text-8xl opacity-10 transform -rotate-12 pointer-events-none select-none">
          🚢
        </div>
      </div>

      {/* Interactive Map Section */}
      <DisasterMap />

      {/* Weather Carousel */}
      <div className="relative group">
        <div className="flex overflow-x-auto gap-4 px-1 pb-4 snap-x snap-mandatory scrollbar-hide">
          {userLocationWeather && (
            <div className="snap-center shrink-0 w-[85vw] sm:w-[400px]">
               <WeatherCard 
                 weather={userLocationWeather} 
                 loading={weatherLoading} 
               />
            </div>
          )}
          <div className="snap-center shrink-0 w-[85vw] sm:w-[400px]">
             <WeatherCard 
               weather={kotaWeather} 
               loading={weatherLoading} 
               aqi={aqiKota}
               marine={marine}
             />
          </div>
          <div className="snap-center shrink-0 w-[85vw] sm:w-[400px]">
             <WeatherCard 
               weather={kabWeather} 
               loading={weatherLoading} 
               aqi={aqiKab}
               // Marine data is mostly relevant for the coast (Kota), but Kab also has coast. 
               // For now, let's show marine only on Kota card to avoid duplication or show it on both if needed.
               // Given "Kota Tegal" is the main coastal city reference usually, we put it there.
             />
          </div>
        </div>
        <div className="flex justify-center gap-2 mt-[-10px]">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
        </div>
        <div className="flex justify-center mt-1">
          <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-gray-400 hover:text-blue-500 transition-colors">
            Weather Data Powered by Open-Meteo.com
          </a>
        </div>
      </div>

      {/* Weather Validation Widget */}
      <WeatherValidation 
        weather={currentWeather} 
        location={weatherLocation} 
        isUserLocation={isUserLocation} 
      />

      {/* Quick Action Tiles */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <button 
          onClick={() => handleQuickAction('rob')}
          className={`flex flex-col text-left p-4 sm:p-5 rounded-[1.5rem] border transition-all relative overflow-hidden group ${
            activeAction === 'rob' ? 'bg-blue-600 border-blue-600 shadow-xl' : 'bg-white border-gray-100'
          }`}
        >
          <span className="mb-2 text-2xl">🌊</span>
          <h4 className={`font-black text-[10px] uppercase tracking-widest ${activeAction === 'rob' ? 'text-white' : 'text-blue-900'}`}>Cek Rob</h4>
          <p className={`text-[9px] font-bold leading-tight mt-1 ${activeAction === 'rob' ? 'text-blue-100' : 'text-blue-400'}`}>Info Pelabuhan.</p>
        </button>
        
        <button 
          onClick={() => handleQuickAction('weather')}
          className={`flex flex-col text-left p-4 sm:p-5 rounded-[1.5rem] border transition-all relative overflow-hidden group ${
            activeAction === 'weather' ? 'bg-orange-600 border-orange-600 shadow-xl' : 'bg-white border-gray-100'
          }`}
        >
          <span className="mb-2 text-2xl">🌦️</span>
          <h4 className={`font-black text-[10px] uppercase tracking-widest ${activeAction === 'weather' ? 'text-white' : 'text-orange-900'}`}>Cuaca {weatherLocation}</h4>
          <p className={`text-[9px] font-bold leading-tight mt-1 ${activeAction === 'weather' ? 'text-orange-100' : 'text-orange-400'}`}>{weatherSubtext}</p>
        </button>
      </div>

      {/* Result Panel */}
      {activeAction && (
        <div className="bg-white rounded-[2rem] p-5 shadow-lg border border-gray-50 animate-in slide-in-from-top-4 duration-500 relative">
          <button onClick={() => setActiveAction(null)} className="absolute top-4 right-4 p-2 text-xl text-gray-300">✕</button>
          <div className="flex gap-3 items-center mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${activeAction === 'rob' ? 'bg-blue-50' : 'bg-orange-50'}`}>
              {activeAction === 'rob' ? '⚓' : '🌦️'}
            </div>
            <h3 className="text-xs font-black tracking-wider text-gray-800 uppercase">Laporan Cepat</h3>
          </div>
          {actionLoading ? (
            <div className="flex flex-col gap-2 items-center py-6">
              <div className="w-6 h-6 rounded-full border-2 border-red-500 animate-spin border-t-transparent"></div>
              <p className="text-[10px] font-bold text-gray-400">Memuat info...</p>
            </div>
          ) : (
            <div className="text-sm text-gray-600 prose-custom">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{actionContent}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/* News Feed Section */}
      <div className="bg-white rounded-[2rem] p-5 shadow-sm border border-gray-100 min-h-[400px]">
        <div className="flex justify-between items-start mb-6">
          <div className="flex gap-3 items-center">
            <div className="p-2 bg-blue-50 rounded-xl">
              <span className="text-lg">🌐</span>
            </div>
            <div>
              <h3 className="text-xs font-black tracking-wider leading-none text-gray-800 uppercase">Kabar Terkini</h3>
              <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest mt-1">
                {news.isCached ? (news.isStale ? '⚠️ Data Lama' : '✅ Terupdate') : '🌐 Langsung'} • {getTimeLabel(news.updatedAt)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleShareNews}
              disabled={loading || !news.text}
              className="p-2 bg-gray-50 rounded-lg transition-transform disabled:opacity-30 active:scale-90"
            >
              🔗
            </button>
            <button 
              onClick={handleManualRefresh}
              disabled={loading}
              className="p-2 bg-gray-50 rounded-lg transition-transform disabled:opacity-30 active:scale-90"
            >
              <span className={`block ${loading ? 'animate-spin' : ''}`}>🔄</span>
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex flex-col gap-4 py-12 animate-pulse">
            <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
            <div className="w-full h-4 bg-gray-200 rounded"></div>
            <div className="w-5/6 h-4 bg-gray-200 rounded"></div>
            <div className="w-4/5 h-4 bg-gray-200 rounded"></div>
            <div className="mt-4 w-full h-32 bg-gray-100 rounded-xl"></div>
          </div>
        ) : (
          <div className="duration-300 animate-in fade-in">
            {news.isStale && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-[9px] text-amber-800 font-bold flex items-center gap-2">
                <span>⚠️</span> Koneksi lambat, menampilkan data tersimpan.
              </div>
            )}
            <div className="mb-6 text-gray-600 prose-custom">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {news.text}
              </ReactMarkdown>
            </div>
            {news.links.length > 0 && (
              <div className="pt-5 mt-5 border-t border-gray-50">
                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-3">Sumber ({news.links.length}):</p>
                <div className="flex flex-col gap-2">
                  {news.links.map((link, idx) => (
                    <a key={idx} href={link.uri} target="_blank" rel="noopener noreferrer"
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-xl border border-gray-100 transition-colors active:bg-gray-100 group"
                    >
                      <span className="text-[10px] font-bold text-gray-700 truncate mr-2 flex-1 min-w-0 group-hover:text-blue-600 transition-colors">{link.title}</span>
                      <span className="text-blue-600 text-[9px] font-black uppercase shrink-0">BUKA ↗</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendation Section */}
      <div className="bg-slate-50 border border-slate-100 rounded-[2rem] p-5 sm:p-6 shadow-sm">
        <div className="flex gap-3 items-start">
          <div className="p-2 text-blue-600 bg-blue-100 rounded-lg shrink-0">
             <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
               <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
             </svg>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-bold text-slate-800">Rekomendasi & Sumber Informasi</h3>
            <p className="mb-3 text-xs leading-relaxed text-slate-600">
              Informasi ini bisa dijadikan kewaspadaan dan pertimbangan untuk melakukan langkah mitigasi dampak ikutan dari kedua kondisi tersebut.
            </p>
            <p className="text-xs leading-relaxed text-slate-600">
              Apabila memerlukan informasi lebih rinci terkait dengan informasi iklim, prakiraan cuaca dan peringatan dini yang lebih detail hingga level kecamatan yang sesuai dengan update kondisi terbaru, Bapak/Ibu dapat menghubungi Kantor Unit Pelaksana Teknis BMKG terdekat atau mengakses media Informasi BMKG di:
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <a href="https://bmkg.go.id" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition">
                🌐 bmkg.go.id
              </a>
              <a href="https://stamet-ahmadyani.bmkg.go.id/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition">
                🌐 stamet-ahmadyani
              </a>
              <a href="https://cuaca.bmkg.go.id" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition">
                🌐 cuaca.bmkg.go.id
              </a>
              <span className="inline-flex items-center px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-600">
                📱 Apps @infoBMKG
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
